import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "tiktok",
  app: "TikTok",
  title: "How to block TikTok on iPhone (and why limits fail)",
  h1: "How to block TikTok",
  description:
    "Screen Time steps that actually hold, why TikTok is harder to close than anything else on your phone, and what to do when the limit stops working.",
  targets: [
    "how to block tiktok on iphone",
    "block tiktok app",
    "stop using tiktok",
    "tiktok screen time limit not working",
  ],
  published: "2026-09-03",
  standfirst:
    "Every other app gives you a moment where the content ends. TikTok removed that moment on purpose, and it is the whole reason this is harder than it should be.",
  takeaways: [
    "Set the limit on the whole Entertainment category rather than on TikTok alone, or you will simply open a different one of them.",
    "Turn on Block at End of Limit and set a Screen Time passcode that is not your unlock code. Without both, the limit is a notification.",
    "TikTok's design has no seam — no page end, no episode, no bottom of the feed — so there is no natural point at which you would have stopped. The stop has to be imposed from outside.",
  ],
  mechanic: [
    "Almost every other feed has a seam in it. Instagram used to end. An article finishes. A video has a length you can see before you start it. A seam is a moment where continuing requires a decision, and a decision is where a person can leave.",
    "TikTok's For You page has none. The next video is already playing before the last one has resolved, the length is hidden until you are in it, and the swipe is the smallest physical gesture on the device. There is no point in the sequence where the app asks you anything, which means there is no point at which you were ever going to stop.",
    "The recommendation system compounds it. TikTok's is trained on watch time above almost everything else, and it gets a signal from every video whether or not you interact — so twenty minutes in, it is showing you things chosen by a model that has learned specifically what keeps you from swiping away. That is not an accusation. It is the stated design.",
  ],
  limitFails:
    "A Screen Time limit on TikTok alone moves the problem rather than solving it: the limit fires, and the same twenty minutes goes into Reels or Shorts instead, which are the same product in a different building. And because TikTok is where most people spend the largest single block, the limit tends to arrive at the worst moment — mid-feed, at the end of a bad day — which is exactly when a one-tap Ignore Limit is easiest to press.",
  instead: [
    "Limit the whole Entertainment category, not the app. That covers the substitute you would otherwise reach for.",
    "Set a Downtime window over the hours you actually lose, which for most people is the last two of the day rather than the working ones.",
    "Delete the app and keep the account. The browser version is deliberately worse, which is the point — the friction is the feature.",
    "If the limit is being dismissed rather than obeyed, the problem is not the limit. Put something between you and the dismissal that costs more than one tap.",
  ],
  verses: [
    {
      verse: {
        text: "Sheol and Abaddon are never satisfied; and a man's eyes are never satisfied.",
        ref: "Proverbs 27:20",
      },
      note: "Written about three thousand years before an infinite feed, and it is the most accurate one-line description of one available. The comparison is to the grave, which is not a moral judgement so much as an observation about capacity: there is no amount of looking that produces the state of having looked enough.",
    },
    {
      verse: {
        text: '"All things are lawful for me," but not all things are expedient. "All things are lawful for me," but I will not be brought under the power of anything.',
        ref: "1 Corinthians 6:12",
      },
      note: "Paul quoting a slogan back at people and adding a condition. The test he applies is not whether the thing is forbidden but whether it has power over him, which is the only useful question to ask about an app. Nobody thinks TikTok is a sin. Plenty of people would struggle to stop for a fortnight.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Do it on the category",
      id: "do-it-on-the-category",
    },
    {
      type: "p",
      text: "The single most common mistake is limiting one app. The hour does not disappear when TikTok closes; it relocates, usually within about four seconds, to whichever other feed is on the phone.",
    },
    {
      type: "p",
      text: "Apple's category limits exist for this and almost nobody uses them. Entertainment and Social Networking together cover nearly everything the relocated hour could go to.",
    },
    {
      type: "h2",
      text: "The passcode is the whole thing",
      id: "the-passcode-is-the-whole-thing",
    },
    {
      type: "p",
      text: "A Screen Time limit without a passcode is a suggestion with a button next to it that says Ignore Limit. With a passcode you set while calm and cannot remember while not, it becomes an actual boundary — and {this is the difference} between people for whom Screen Time works and people for whom it never has.",
      links: {
        "this is the difference": "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "note",
      text: "Do not use your unlock code. If the Screen Time passcode is the same four digits your thumb types forty times a day, it is not a barrier, it is a formality.",
    },
    {
      type: "h2",
      text: "What to do with the time",
      id: "what-to-do-with-the-time",
    },
    {
      type: "p",
      text: "A blocked hour is not automatically a good hour. The first week of any of this is mostly boredom, and boredom is what the app was solving. Deciding in advance what the hour is for — a book, a walk, a person, ten minutes of {something said out loud} — is the difference between a fortnight and a habit.",
      links: {
        "something said out loud": "/prayers/prayer-before-you-open-your-phone",
      },
    },
  ],
  faq: [
    {
      q: "How do I block TikTok on my iPhone?",
      a: "Settings → Screen Time → App Limits → Add Limit, then choose Entertainment rather than TikTok alone, set the amount, and turn on Block at End of Limit with a Screen Time passcode that is not your unlock code.",
    },
    {
      q: "Why does my TikTok screen time limit not work?",
      a: "Almost always one of two things: there is no Screen Time passcode, so Ignore Limit is one tap away, or the limit is on TikTok alone and the time has moved to Reels or Shorts.",
    },
    {
      q: "Should I delete TikTok or just limit it?",
      a: "Deleting the app and keeping the account is the strongest option that does not involve a third-party tool, because the browser version is deliberately worse. Limits are for people who genuinely need the app for work.",
    },
  ],
  relatedApps: ["instagram", "youtube"],
  related: ["screen-time-limits-dont-work", "christian-app-blocker-guide"],
  relatedPrayers: [
    "prayer-before-you-open-your-phone",
    "prayer-for-self-control",
  ],
};
